import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    handler: (onClose: () => void) => void;
  };
  dismissible?: boolean;
  hideIcon?: boolean;
  // MAP-1579: correlates a background task's progress notification with its
  // completion notification. See NotificationService.
  taskKey?: string;
}

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div class="notification notification-{{notification.type}}"
         [class.notification-dismissing]="isDismissing">
      <div class="notification-main">
        <div class="notification-icon" *ngIf="!notification.hideIcon">
          <mat-icon>{{getIcon()}}</mat-icon>
        </div>
        <div class="notification-content">
          <div class="notification-header" *ngIf="notification.title">
            <div class="notification-title">
              {{notification.title}}
            </div>
            <button *ngIf="notification.dismissible !== false"
                    type="button"
                    class="notification-close-btn"
                    (click)="onClose()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="notification-message" [innerHTML]="getFormattedMessage()">
          </div>
        </div>
        <button *ngIf="notification.dismissible !== false && !notification.title"
                type="button"
                class="notification-close-btn"
                (click)="onClose()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div *ngIf="notification.action" class="notification-action-row">
        <button mat-stroked-button
                class="notification-action-btn"
                (click)="onActionClick()">
          {{notification.action.label}}
        </button>
      </div>
      <div *ngIf="notification.type === 'loading'" class="loading-bar"></div>
    </div>
  `,
  styles: [`
    .notification {
      display: flex;
      flex-direction: column;
      padding: 0;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 12px;
      min-width: 320px;
      max-width: 500px;
      position: relative;
      overflow: hidden;
      transform: translateX(0);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .notification-main {
      display: flex;
      align-items: flex-start;
      padding: 16px 20px;
      width: 100%;
    }

    .notification-action-row {
      padding: 10px;
      border-top: 1px solid rgba(89, 112, 128, 0.1);
      margin-top: 0;
      display: flex;
      justify-content: flex-end;
    }

    .notification-dismissing {
      transform: translateX(100%);
      opacity: 0;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .notification-icon {
      margin-right: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .notification-content {
      flex: 1;
      min-width: 0;
    }

    .notification-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }

    .notification-title {
      font-weight: 600;
      font-size: 16px;
      line-height: 1.3;
      flex: 1;
      margin-right: 8px;
    }

    .notification-header .notification-close-btn {
      width: 20px;
      height: 20px;
    }

    .notification-header .notification-close-btn mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .notification-message {
      font-size: 14px;
      line-height: 1.4;
      opacity: 0.9;
    }

    /* Quoted echo of the user's input (e.g. their failed prompt). Monospace
       block users can select and copy to paste back into a new attempt. */
    .notification-message .notification-prompt-echo {
      display: block;
      margin-top: 10px;
      padding: 8px 12px;
      border-radius: 6px;
      background-color: rgba(0, 0, 0, 0.06);
      font-family: "Courier New", Courier, monospace;
      font-size: 13px;
      line-height: 1.4;
      white-space: pre-wrap;
      word-break: break-word;
      user-select: text;
      cursor: text;
    }

    .notification-actions {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-left: 16px;
      flex-shrink: 0;
    }

    .notification-action-btn {
      --mdc-text-button-label-text-size: 14px;
      font-weight: 600;
      border-radius: 8px;
      padding: 8px 16px;
      height: auto;
      line-height: 1;
    }

    .notification-close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: none;
      border-radius: 8px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s ease;
      padding: 0;
      margin: 0;
    }

    .notification-close-btn:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .notification-close-btn:focus {
      outline: 2px solid rgba(0, 0, 0, 0.2);
      outline-offset: 2px;
    }

    .notification-close-btn mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      line-height: 1;
    }

    .notification-action-btn {
      font-size: 14px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px 16px;
      height: auto;
      line-height: 1.4;
      min-width: 80px;
      transition: all 0.2s ease-in-out;
    }

    .notification-action-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    /* Success */
    .notification-success {
      background: linear-gradient(135deg, #E1F5E1 0%, #D4EDD4 100%);
      color: #2E7D32;
      border-left: 4px solid #4CAF50;
    }

    .notification-success .notification-icon {
      background-color: rgba(76, 175, 80, 0.15);
      color: #4CAF50;
    }

    .notification-success .notification-action-btn {
      --mdc-outlined-button-outline-color: #4CAF50;
      --mdc-outlined-button-label-text-color: #4CAF50;
      border-color: #4CAF50;
      color: #4CAF50;
    }

    .notification-success .notification-action-btn:hover {
      --mdc-outlined-button-hover-state-layer-color: #4CAF50;
      background-color: rgba(76, 175, 80, 0.08);
    }

    .notification-success .notification-close-btn {
      color: #597080;
    }

    .notification-success .notification-close-btn:hover {
      background-color: rgba(76, 175, 80, 0.1);
    }

    /* Error */
    .notification-error {
      background: linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%);
      color: #C62828;
      border-left: 4px solid #F44336;
    }

    .notification-error .notification-icon {
      background-color: rgba(244, 67, 54, 0.15);
      color: #F44336;
    }

    .notification-error .notification-action-btn {
      --mdc-outlined-button-outline-color: #F44336;
      --mdc-outlined-button-label-text-color: #F44336;
      border-color: #F44336;
      color: #F44336;
    }

    .notification-error .notification-action-btn:hover {
      --mdc-outlined-button-hover-state-layer-color: #F44336;
      background-color: rgba(244, 67, 54, 0.08);
    }

    .notification-error .notification-close-btn {
      color: #597080;
    }

    .notification-error .notification-close-btn:hover {
      background-color: rgba(244, 67, 54, 0.1);
    }

    /* Warning */
    .notification-warning {
      background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%);
      color: #E65100;
      border-left: 4px solid #FF9800;
    }

    .notification-warning .notification-icon {
      background-color: rgba(255, 152, 0, 0.15);
      color: #FF9800;
    }

    .notification-warning .notification-action-btn {
      --mdc-outlined-button-outline-color: #FF9800;
      --mdc-outlined-button-label-text-color: #FF9800;
      border-color: #FF9800;
      color: #FF9800;
    }

    .notification-warning .notification-action-btn:hover {
      --mdc-outlined-button-hover-state-layer-color: #FF9800;
      background-color: rgba(255, 152, 0, 0.08);
    }

    .notification-warning .notification-close-btn {
      color: #597080;
    }

    .notification-warning .notification-close-btn:hover {
      background-color: rgba(255, 152, 0, 0.1);
    }

    /* Info */
    .notification-info {
      background: linear-gradient(135deg, #E1E6EA 0%, #D6DDE3 100%);
      color: #597080;
      border-left: 4px solid #2196F3;
    }

    .notification-info .notification-icon {
      background-color: rgba(33, 150, 243, 0.15);
      color: #2196F3;
    }

    .notification-info .notification-action-btn {
      --mdc-outlined-button-outline-color: #2196F3;
      --mdc-outlined-button-label-text-color: #2196F3;
      border-color: #2196F3;
      color: #2196F3;
    }

    .notification-info .notification-action-btn:hover {
      --mdc-outlined-button-hover-state-layer-color: #2196F3;
      background-color: rgba(33, 150, 243, 0.08);
    }

    .notification-info .notification-close-btn {
      color: #597080;
    }

    .notification-info .notification-close-btn:hover {
      background-color: rgba(33, 150, 243, 0.1);
    }

    /* Loading */
    .notification-loading {
      background: linear-gradient(135deg, #E1E6EA 0%, #CFD8DF 100%);
      color: #597080;
      border-left: 4px solid #607D8B;
    }

    .notification-loading .notification-icon {
      background-color: rgba(96, 125, 139, 0.15);
      color: #607D8B;
    }

    .notification-loading .notification-icon mat-icon {
      animation: spin 1.5s linear infinite;
    }

    .notification-loading .notification-close-btn {
      color: #597080;
    }

    .loading-bar {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      background: linear-gradient(90deg, transparent, rgba(89, 112, 128, 0.6), transparent);
      animation: loadingBar 2s infinite;
      width: 100%;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes loadingBar {
      0% { transform: translateX(-100%); }
      50% { transform: translateX(0%); }
      100% { transform: translateX(100%); }
    }
  `]
})
export class NotificationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() notification!: Notification;
  @Output() dismiss = new EventEmitter<string>();
  @Output() actionClick = new EventEmitter<Notification>();

  isDismissing = false;
  private autoCloseTimer?: number;

  ngOnInit() {
    this.restartAutoClose();
  }

  ngOnChanges(changes: SimpleChanges) {
    // The service re-targets a notification in place (same id, new object) when
    // a background task moves on to its next stage; the duration may change
    // with it, so the auto-close timer has to be rearmed.
    if (changes['notification'] && !changes['notification'].firstChange) {
      this.restartAutoClose();
    }
  }

  ngOnDestroy() {
    this.clearAutoClose();
  }

  private restartAutoClose() {
    this.clearAutoClose();
    if (this.notification.duration && this.notification.duration > 0) {
      this.autoCloseTimer = window.setTimeout(() => {
        this.onClose();
      }, this.notification.duration);
    }
  }

  private clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = undefined;
    }
  }

  getIcon(): string {
    switch (this.notification.type) {
      case 'success': return 'check_circle';
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'loading': return 'refresh';
      default: return 'notifications';
    }
  }

  getFormattedMessage(): string {
    return this.notification.message.replace(/\n/g, '<br>');
  }

  onClose() {
    this.isDismissing = true;
    setTimeout(() => {
      this.dismiss.emit(this.notification.id);
    }, 400);
  }

  onActionClick() {
    if (this.notification.action) {
      this.notification.action.handler(this.onClose.bind(this));
      this.actionClick.emit(this.notification);
    }
  }
}
